/**
 * Every value an hours registration or an expense claim can hold.
 *
 * Three, in both `TimeEntryStatus` and `ExpenseClaimStatus` — see `prisma/schema.prisma`.
 *
 * It was five until 12-08-2026. `KLAARGEZET_VOOR_VERLONING` and `VERLOOND` were dropped from both
 * enums (api migration `20260812191000_drop_verloning_enum_waarden`); payroll progress moved to the
 * `payRunId` FK, which the app does not receive. So the app can no longer tell that a line has been
 * paid out — not from the status, anyway.
 *
 * Keeping them listed here was not harmless: `STATUSES_BY_STATE` feeds a _server-side_ status
 * filter, so "Goedgekeurd" would have asked the api for a value its enum no longer has.
 */
export const ENTRY_STATUSES = ["PENDING", "ACCEPTED", "REJECTED"] as const;

export type EntryStatus = (typeof ENTRY_STATUSES)[number];

/**
 * What the employee sees. Three states.
 *
 * There was a fourth, `paid` — "it is on your payslip", the one status that meant money had moved.
 * It is gone because the status it read from is gone (see `ENTRY_STATUSES`): payroll progress now
 * lives in a `payRunId` FK the app never receives. Leaving `paid` in would have meant a filter that
 * can only come back empty and a chip that can never render.
 *
 * Worth bringing back the moment the api exposes payroll state to the app — it is the most
 * interesting thing the chain has to say. It just cannot be said from `status` any more.
 *
 * `entry-status.state.paid` en `entry-status.hint.paid` staan daarom nog in beide bundles terwijl
 * `toEntryState` nooit `"paid"` teruggeeft. Dat is geen vergeten opruimwerk: de zinnen zijn in twee
 * talen geschreven en nagekeken, en ze kosten niets zolang ze ongebruikt zijn. Haal ze pas weg als
 * besloten is dat de app payroll-state nooit krijgt.
 */
export type EntryState = "pending" | "rejected" | "approved";

/** Which raw statuses sit behind each state — the shape a server-side status filter needs. */
export const STATUSES_BY_STATE: Record<EntryState, readonly EntryStatus[]> = {
  pending: ["PENDING"],
  rejected: ["REJECTED"],
  approved: ["ACCEPTED"],
};

const STATE_BY_STATUS: Record<EntryStatus, EntryState> = {
  PENDING: "pending",
  REJECTED: "rejected",
  ACCEPTED: "approved",
};

/**
 * The employee-facing state for a raw status.
 *
 * Returns `null` for an absent status, and for any value we have not met yet — a future enum member
 * should render nothing rather than its own name. Silence is a better failure than jargon.
 */
export function toEntryState(status: string | null | undefined): EntryState | null {
  if (!status) {
    return null;
  }
  return STATE_BY_STATUS[status as EntryStatus] ?? null;
}

/** Settled: agreed and out of the employee's hands. */
export function isSettled(state: EntryState | null): boolean {
  return state === "approved";
}

/** The ball is back with the employee — the one state that earns a button instead of a chip. */
export function needsAttention(state: EntryState | null): boolean {
  return state === "rejected";
}
